// Fonctions pures extraites de useNomenclature (stats, filtrage, export).
// Aucune dépendance React : facilite le test et allège le hook.

import * as XLSX from 'xlsx';

// Ligne "plate" normalisée pour l'export Excel.
function toExcelRow(n) {
  return {
    'Code': n.code || '',
    'Nom': n.name || '',
    'Catégorie': n.category || 'Non catégorisé',
    'Définition': n.definition || '',
    'Exclusions': n.exclusions || '',
    'Statut': n.status || (n.is_active ? 'active' : 'inactive'),
    'Dispositifs': (n.dispositifs || []).join('; '),
    'Applicable Membre': n.applicable_devenir_membre ? 'Oui' : 'Non'
  };
}

// Nettoie un nom d'onglet Excel (max 31 car., caractères interdits : : \ / ? * [ ]).
function sanitizeSheetName(name, used) {
  let base = String(name || 'Sans nom').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 28) || 'Sans nom';
  let candidate = base;
  let i = 2;
  while (used.has(candidate)) {
    candidate = `${base.slice(0, 26)} ${i}`;
    i += 1;
  }
  used.add(candidate);
  return candidate;
}

// Export Excel multi-onglets : "Toutes" + un onglet par catégorie + un onglet par dispositif.
// sheetOptions (optionnel) : { categories: string[]|null, dispositifs: string[]|null }
// Si une liste est fournie, seuls ces onglets sont générés ; null/undefined = tous.
export function exportNomenclaturesXlsx(dataToExport, collectionName, sheetOptions = {}) {
  const wb = XLSX.utils.book_new();
  const used = new Set();
  const catFilter = sheetOptions.categories ? new Set(sheetOptions.categories) : null;
  const dispFilter = sheetOptions.dispositifs ? new Set(sheetOptions.dispositifs) : null;

  // Onglet global
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(dataToExport.map(toExcelRow)),
    sanitizeSheetName('Toutes', used)
  );

  // Un onglet par catégorie (filtré si demandé)
  const byCategory = {};
  dataToExport.forEach(n => {
    const cat = n.category || 'Non catégorisé';
    (byCategory[cat] = byCategory[cat] || []).push(n);
  });
  Object.keys(byCategory).sort().forEach(cat => {
    if (catFilter && !catFilter.has(cat)) return;
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(byCategory[cat].map(toExcelRow)),
      sanitizeSheetName(`Cat. ${cat}`, used)
    );
  });

  // Un onglet par dispositif (filtré si demandé)
  const byDispositif = {};
  dataToExport.forEach(n => {
    (n.dispositifs || []).forEach(d => {
      (byDispositif[d] = byDispositif[d] || []).push(n);
    });
  });
  Object.keys(byDispositif).sort().forEach(d => {
    if (dispFilter && !dispFilter.has(d)) return;
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(byDispositif[d].map(toExcelRow)),
      sanitizeSheetName(`Disp. ${d}`, used)
    );
  });

  const dateSuffix = new Date().toISOString().split('T')[0];
  XLSX.writeFile(wb, `nomenclatures_${collectionName || 'export'}_${dateSuffix}.xlsx`);
}

// Récapitulatif de ce que produira l'export Excel multi-onglets.
export function summarizeExport(data) {
  const catMap = {};
  const dispMap = {};
  data.forEach(n => {
    const cat = n.category || 'Non catégorisé';
    catMap[cat] = (catMap[cat] || 0) + 1;
    (n.dispositifs || []).forEach(d => {
      dispMap[d] = (dispMap[d] || 0) + 1;
    });
  });
  const categories = Object.keys(catMap).sort().map(name => ({ name, count: catMap[name] }));
  const dispositifs = Object.keys(dispMap).sort().map(name => ({ name, count: dispMap[name] }));
  return {
    total: data.length,
    categories,
    dispositifs,
    sheetCount: 1 + categories.length + dispositifs.length // "Toutes" + catégories + dispositifs
  };
}

export function computeStats(data) {
  const newStats = {
    total: data.length,
    active: data.filter(n => n.status === 'active' || n.is_active).length,
    inactive: data.filter(n => n.status === 'inactive').length,
    pending: data.filter(n => n.status === 'pending').length,
    archived: data.filter(n => n.status === 'archived' || n.is_archived).length,
    byCategory: {},
    byDispositif: {}
  };

  data.forEach(n => {
    const cat = n.category || 'Non catégorisé';
    newStats.byCategory[cat] = (newStats.byCategory[cat] || 0) + 1;

    (n.dispositifs || []).forEach(d => {
      newStats.byDispositif[d] = (newStats.byDispositif[d] || 0) + 1;
    });
  });

  return newStats;
}

export function matchesNomenclatureFilters(n, { searchTerm, selectedCategory, selectedStatus, selectedDispositif }) {
  const matchSearch = !searchTerm ||
    n.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.definition?.toLowerCase().includes(searchTerm.toLowerCase());

  const matchCategory = selectedCategory === 'all' || n.category === selectedCategory;

  // V36.19 — Robustesse : prend en compte les valeurs FR héritées (statut/categorie) et alias
  const matchStatus = (() => {
    if (selectedStatus === 'all') return true;
    if (selectedStatus === 'archived') {
      return n.is_archived === true
        || n.status === 'archived'
        || n.status === 'archive'
        || n.statut === 'archived';
    }
    if (selectedStatus === 'active') {
      return (n.is_active === true && !n.is_archived)
        || n.status === 'active'
        || n.status === 'actif'
        || n.statut === 'actif';
    }
    if (selectedStatus === 'inactive') {
      return n.is_active === false
        || n.status === 'inactive'
        || n.status === 'inactif'
        || n.statut === 'inactif';
    }
    if (selectedStatus === 'pending') {
      return n.status === 'pending'
        || n.status === 'en_attente'
        || n.statut === 'pending';
    }
    return n.status === selectedStatus;
  })();

  const matchDispositif = selectedDispositif === 'all'
    || (Array.isArray(n.dispositifs) && n.dispositifs.includes(selectedDispositif));

  return matchSearch && matchCategory && matchStatus && matchDispositif;
}

export function buildExportPayload(format, dataToExport, collectionName) {
  const dateSuffix = new Date().toISOString().split('T')[0];

  if (format === 'json') {
    return {
      content: JSON.stringify({
        nomenclatures: dataToExport,
        exported_at: new Date().toISOString(),
        collection: collectionName || 'Unknown',
        version: '2.0'
      }, null, 2),
      filename: `nomenclatures_${collectionName || 'export'}_${dateSuffix}.json`,
      type: 'application/json'
    };
  }

  if (format === 'csv') {
    const headers = ['Code', 'Nom', 'Catégorie', 'Définition', 'Exclusions', 'Statut', 'Dispositifs', 'Applicable Membre'];
    const rows = dataToExport.map(n => [
      n.code,
      n.name,
      n.category,
      n.definition?.replace(/[\n\r]/g, ' '),
      n.exclusions?.replace(/[\n\r]/g, ' '),
      n.status || (n.is_active ? 'active' : 'inactive'),
      (n.dispositifs || []).join('; '),
      n.applicable_devenir_membre ? 'Oui' : 'Non'
    ]);
    return {
      content: [headers.join(';'), ...rows.map(r => r.map(c => `"${c || ''}"`).join(';'))].join('\n'),
      filename: `nomenclatures_${collectionName || 'export'}_${dateSuffix}.csv`,
      type: 'text/csv;charset=utf-8;'
    };
  }

  // excel
  const headers = ['Code', 'Nom', 'Catégorie', 'Définition', 'Exclusions', 'Statut', 'Dispositifs'];
  const rows = dataToExport.map(n => [
    n.code, n.name, n.category, n.definition, n.exclusions,
    n.status || (n.is_active ? 'active' : 'inactive'),
    (n.dispositifs || []).join('; ')
  ]);
  return {
    content: [headers.join('\t'), ...rows.map(r => r.join('\t'))].join('\n'),
    filename: `nomenclatures_${collectionName || 'export'}_${dateSuffix}.xls`,
    type: 'application/vnd.ms-excel;charset=utf-8;'
  };
}
